"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useMemo } from "react";
import { Phone, Calendar, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const PRACTICE_NAME = "Alpha Dental – Universal City";
const PHONE_DISPLAY = "(210) 775-5177";
const PHONE_TEL = "+12107755177";
const ADDRESS_LINE1 = "916 Coronado Blvd";
const CITY = "Universal City";
const STATE = "TX";
const ZIP = "78148";
const FULL_ADDRESS = `${ADDRESS_LINE1}, ${CITY}, ${STATE} ${ZIP}`;
type Service = {
  id: number;
  title: string;
  description: string;
};
type FinancingFeature = {
  id: number;
  title: string;
  description: string;
};
export default function ScheduleAnAppointment() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const jsonLd = useMemo(() => {
    const url = typeof window !== "undefined" ? window.location.origin : "https://ddsalpha.com";
    return {
      "@context": "https://schema.org",
      "@type": "Dentist",
      name: PRACTICE_NAME,
      url,
      telephone: PHONE_TEL,
      address: {
        "@type": "PostalAddress",
        streetAddress: ADDRESS_LINE1,
        addressLocality: CITY,
        addressRegion: STATE,
        postalCode: ZIP,
        addressCountry: "US"
      },
      description: "Dental services, accepted insurance, and financing options for patients in Universal City, TX near Randolph Air Force Base. General dentistry, emergency care, implants, and full-arch implants.",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Dental Services & Patient Options",
        itemListElement: [...services.map(s => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: s.title,
            description: s.description
          }
        })), {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Dental Insurance Verification",
            description: "Benefits review, eligibility checks, and upfront estimates before treatment."
          }
        }, {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Dental Financing Options",
            description: "Flexible monthly payments through third-party financing partners for qualified patients."
          }
        }]
      }
    };
  }, []);
  return <section id="schedule-an-appointment" ref={ref} aria-label="Dental services, insurance, and financing in Universal City, Texas" className={["bg-[#F7DDE2] px-4 py-12", "w-full", "overflow-visible", "scroll-mt-24", "transition-all duration-[var(--duration-medium)] will-change-opacity", isVisible ? "opacity-100" : "opacity-0", "block"].join(" ")}>
      {}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(jsonLd)
    }} />

      <div className="mx-auto max-w-6xl">
        {}
        <header className="mb-10 text-center">
          <h2 className="font-display text-3xl font-bold text-[#3F2E32] mb-3">
            <strong>Dental Services in Universal City, TX</strong>
          </h2>

          <p className="font-sans text-base text-[#5A4449] max-w-4xl mx-auto leading-relaxed">
            <strong>{PRACTICE_NAME}</strong> provides complete{" "}
            <strong>general dentistry</strong>, <strong>dental implants</strong>,
            and <strong>All-on-4 / All-on-X full-arch implants</strong> with
            caring, modern treatment for the whole family.
          </p>

          <p className="mt-3 text-sm text-[#5A4449]">
            Visit us at <strong>{FULL_ADDRESS}</strong> — near{" "}
            <strong>Randolph Air Force Base</strong>.
          </p>
        </header>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" role="list">
          {services.map((service, i) => <Card key={service.id} data-index={i} role="listitem" className="bg-[#F4C9D3] shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] hover:-translate-y-1 border-none">
              <CardHeader className="pb-3">
                <CardTitle className="font-display text-lg font-semibold text-[#3F2E32]">
                  <strong>{service.title}</strong>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-sans text-sm text-[#5A4449] leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>)}
        </div>

        <p className="font-sans text-sm text-center text-[#5A4449] mb-10 italic">
          Serving <strong>Universal City</strong>, <strong>Live Oak</strong>,{" "}
          <strong>Converse</strong>, and <strong>San Antonio</strong> near{" "}
          <strong>Randolph AFB</strong>.
        </p>

        {}
        <div className="mb-10">
          <Card className="bg-[#F4C9D3] shadow-soft border-none">
            <CardHeader className="text-center pb-4">
              <CardTitle className="font-display text-2xl font-bold text-[#3F2E32]">
                <strong>Dental Insurance Accepted in Universal City, TX</strong>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="font-sans text-sm text-[#5A4449] text-center leading-relaxed">
                We accept <strong>most PPO plans</strong>,{" "}
                <strong>Medicaid</strong>, <strong>TRICARE</strong>, and many{" "}
                <strong>Medicare Advantage</strong> plans. Our team will{" "}
                <strong>verify your benefits</strong> and provide{" "}
                <strong>clear, upfront estimates</strong> before treatment
                begins.
              </p>

              <h3 className="sr-only">Common insurance plans</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-4">
                {insuranceProviders.map((provider, i) => <div key={provider} data-index={i} className="bg-white/50 rounded-md py-2 px-3 text-center font-sans text-sm text-[#5A4449] font-medium" aria-label={`Insurance plan: ${provider}`}>
                    <strong>{provider}</strong>
                  </div>)}
              </div>

              <p className="font-sans text-xs text-[#5A4449] text-center pt-4 italic">
                Insurance logos are for identification only. Coverage and
                benefits vary by plan. Please contact our office to verify your
                specific coverage.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div className="mb-10">
          <Card className="bg-[#F4C9D3] shadow-soft border-none">
            <CardHeader className="text-center pb-4">
              <CardTitle className="font-display text-2xl font-bold text-[#3F2E32]">
                <strong>Dental Financing Plans Available</strong>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {financingFeatures.map((feature, i) => <div key={feature.id} data-index={i} className="flex flex-col items-center text-center gap-2">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#C27A7E]/20">
                      <CircleCheck className="w-6 h-6 text-[#C27A7E]" />
                    </div>
                    <h3 className="font-display text-base font-semibold text-[#3F2E32]">
                      <strong>{feature.title}</strong>
                    </h3>
                    <p className="font-sans text-sm text-[#5A4449]">
                      {feature.description}
                    </p>
                  </div>)}
              </div>

              <div className="pt-2">
                <p className="font-sans text-sm text-[#5A4449] text-center mb-3 font-medium">
                  Financing Partners:
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  {financingPartners.map((partner, i) => <div key={partner} data-index={i} className="bg-white/50 rounded-md py-2 px-4 font-sans text-sm text-[#5A4449] font-semibold" aria-label={`Financing partner: ${partner}`}>
                      <strong>{partner}</strong>
                    </div>)}
                </div>
              </div>

              <div className="pt-2 text-center">
                <Link to={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 font-sans text-base font-semibold text-[#C27A7E] hover:text-[#A05D63] transition-colors" aria-label={`Call ${PRACTICE_NAME} about dental financing at ${PHONE_DISPLAY}`}>
                  <Phone className="w-5 h-5" />
                  <strong>Call {PHONE_DISPLAY} to Ask About Financing</strong>
                </Link>
              </div>

              <p className="font-sans text-xs text-[#5A4449] text-center pt-1 italic">
                Approval and terms depend on provider. Subject to credit
                approval.
              </p>
            </CardContent>
          </Card>
        </div>

        {}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button asChild className="bg-[#C27A7E] text-white hover:bg-[#A05D63] shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-medium)] px-8 py-5 text-base font-semibold w-full sm:w-auto">
            <Link to={`tel:${PHONE_TEL}`} className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              <strong>Call Now</strong>
            </Link>
          </Button>

          <Button asChild variant="outline" className="border-2 border-[#D6BBA3] text-[#5A4449] bg-transparent hover:bg-[#D6BBA3]/20 shadow-soft hover:shadow-medium hover:-translate-y-0.5 transition-all duration-[var(--duration-medium)] px-8 py-5 text-base font-semibold w-full sm:w-auto">
            <Link to="https://www.appointnow.com/?P=4826&O=101&PT=0&campaignID=" newTab className="flex items-center gap-2 justify-center" aria-label="Schedule a dental appointment online">
              <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
              <strong>Schedule With Us</strong>
            </Link>
          </Button>
        </div>

        {}
        <div className="mt-8 text-center text-xs text-[#5A4449]">
          <p>
            <strong>{PRACTICE_NAME}</strong> · <strong>{FULL_ADDRESS}</strong> ·{" "}
            <strong>{PHONE_DISPLAY}</strong>
          </p>
        </div>
      </div>
    </section>;
}
const services: Service[] = [{
  id: 1,
  title: "Dental Exams & Checkups",
  description: "Comprehensive dental exams help detect cavities, gum disease, and bite issues early so you can protect your oral health."
}, {
  id: 2,
  title: "Teeth Cleaning & Preventive Care",
  description: "Professional cleanings remove plaque and tartar to prevent cavities and gum disease for healthier smiles."
}, {
  id: 3,
  title: "Digital X-Rays & Oral Cancer Screening",
  description: "Low-radiation digital X-rays and routine screenings support accurate diagnosis and early detection."
}, {
  id: 4,
  title: "Tooth-Colored Fillings",
  description: "Natural-looking composite fillings repair cavities and small chips while preserving healthy tooth structure."
}, {
  id: 5,
  title: "Crowns & Bridges",
  description: "Dental crowns strengthen damaged teeth, and bridges replace missing teeth to restore your bite and function."
}, {
  id: 6,
  title: "Root Canal Therapy",
  description: "Root canal treatment can save an infected tooth by removing infection and sealing the tooth for long-term comfort."
}, {
  id: 7,
  title: "Gum Disease Treatment",
  description: "Periodontal evaluations and deep cleanings (scaling and root planing) reduce inflammation and support healthier gums."
}, {
  id: 8,
  title: "Extractions & Wisdom Teeth",
  description: "Gentle extractions with clear aftercare, plus wisdom teeth evaluation and replacement options when needed."
}, {
  id: 9,
  title: "Emergency Dentistry",
  description: "Fast care for toothaches, broken teeth, swelling, and infections with a focus on quick pain relief and solutions."
}, {
  id: 10,
  title: "Cosmetic Dentistry",
  description: "Cosmetic dental care that improves tooth shape, color, and symmetry with natural-looking results."
}, {
  id: 11,
  title: "Dental Implants",
  description: "Dental implants replace missing teeth with a stable, natural-feeling option that supports chewing and bone health."
}, {
  id: 12,
  title: "All-on-4 / All-on-X Full-Arch Implants",
  description: "Full-arch implants restore a full upper or lower set of teeth using strategically placed implants for strong support."
}];
const insuranceProviders: string[] = ["Delta Dental", "Cigna", "Aetna", "MetLife", "Guardian", "Humana", "UnitedHealthcare", "Blue Cross Blue Shield", "Ameritas", "Principal", "United Concordia", "GEHA", "Medicaid", "TRICARE", "Medicare Advantage"];
const financingFeatures: FinancingFeature[] = [{
  id: 1,
  title: "Flexible Monthly Payment Options",
  description: "Get the dental care you need now and pay over time with manageable monthly payments (subject to approval)."
}, {
  id: 2,
  title: "Options for Many Credit Profiles",
  description: "Financing options may be available for a wide range of credit situations through multiple partners."
}, {
  id: 3,
  title: "Simple, Transparent Pricing",
  description: "We review your treatment plan, expected costs, and payment options before you start — so you can move forward confidently."
}];
const financingPartners: string[] = ["CareCredit", "Sunbit", "Cherry"];